import { getPowerGenerationChart } from "$/api/market-monitoring/fundamentals.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import MultiAreaGraph from "$/lib/components/graphs/MultiAreaGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { ENERGY_KPIS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const PowerGenerationCharts = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-power-generation-chart", dateRange],
    queryFn: () =>
      getPowerGenerationChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const keys = useMemo(() => {
    const presetKeys = Object.keys(ENERGY_KPIS);

    const dataKeys = getArrayKeys(data?.data ?? []);

    const filteredKeys = presetKeys.filter((key) => dataKeys.includes(key));

    return filteredKeys.reverse();
  }, [data]);

  const percentageKeys = useMemo(() => {
    const presetKeys = Object.keys(ENERGY_KPIS);

    const dataKeys = getArrayKeys(data?.percentageData ?? []);

    const filteredKeys = presetKeys.filter((key) => dataKeys.includes(key));

    return filteredKeys.reverse();
  }, [data]);

  const colors = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color,
    }),
    {},
  );

  const displayNames = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].label,
    }),
    {},
  );

  const result = getMinMax(data?.data ?? [], keys, false);
  const percentageResult = getMinMax(
    data?.percentageData ?? [],
    percentageKeys,
    false,
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  return (
    <DisplayBox
      title="Production électrique par filière en France"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullHeight fullWidth className="gap-4">
            <Flexbox fullWidth className="h-1/2 gap-1">
              <p className="font-bold text-warning">Absolu</p>
              <MultiAreaGraph
                data={data?.data ?? []}
                keys={keys}
                xAxisDataKey="date"
                isStacked
                unit=" GWh"
                domain={[result.min.value, result.max.value]}
                colors={colors}
                theme="orange"
                displayNames={displayNames}
                heightPercent={44}
                leftMargin={-20}
                reverseTooltipOrder
              />
            </Flexbox>

            <Flexbox fullWidth className="h-1/2 gap-1">
              <p className="font-bold text-warning">Percentage</p>
              <MultiAreaGraph
                data={data?.percentageData ?? []}
                keys={percentageKeys}
                xAxisDataKey="date"
                isStacked
                domain={[
                  percentageResult.min.value,
                  percentageResult.max.value,
                ]}
                unit=" %"
                colors={colors}
                theme="orange"
                displayNames={displayNames}
                heightPercent={44}
                leftMargin={-20}
                reverseTooltipOrder
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="orange"
          />

          <GraphFilter
            title="Légende"
            data={Object.entries(ENERGY_KPIS).map(([, i]) => ({
              name: i.label,
              color: i.color,
            }))}
            // data={[...keys].reverse().map((key) => ({
            //   name: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].label,
            //   color: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color,
            // }))}
            handleOnChange={() => {}}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default PowerGenerationCharts;
